import React from "react";
import { Text, View, ScrollView, KeyboardAvoidingView } from "react-native";

import Utils, { hasItems } from "../../../../helpers/utils";
import appStyles from "../../../appStyles";
import ArchiveElementsFabButton from "./ArchiveElementsFabButton";
import ElementsHelper from "../ElementsHelper";
import ElementsList from "../list/ElementsList";
import Header from "../../common/header";
import ManaConfirmDialog from "../../common/dialog/ManaConfirmDialog";
import Search from "../../common/search-bar/Search";
import AppTheme from "../../AppTheme";
import appPages from "../../../../appPages";

export default class ArchiveElementsPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    const elements =
      ElementsHelper.filterResults(this.controller, {
        showBaseSets: false,
        sortBy: "is_active",
        showArchived: true,
      }) || [];

    elements.forEach((element) => {
      element.is_selected = element.is_active == 0;
    });

    this.state = {
      isLoading: true,
      elements,
      allElements: elements.slice(0),
      showEditPanel: false,
      openConfirmation: false,
      selectedIds: new Set(),
      unarchivedIds: new Set(),
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.componentDidFocus();
        },
      );
    } else {
      this.filterResults({ showBaseSets: false });
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  componentDidFocus = async () => {
    this.filterResults();
  };

  // ------------------------------------- FILTER -------------------------------------
  filterResults = (filters = {}) => {
    const { allElements } = this.state;
    const { term } = filters;

    let elements = allElements.slice(0);
    if (term) {
      const pattern = Utils.getSearchPattern(term);

      elements = elements.filter((a) => {
        return a.name && a.name.match(pattern);
      });
    }

    this.setState((state) => ({
      ...state,
      isLoading: false,
      elements,
    }));
  };

  // ------------------------------------- ARCHIVE -------------------------------------
  updateSelectedElements = (element, isSelected) => {
    let { selectedIds: ids, unarchivedIds } = this.state;

    if (isSelected) {
      ids.add(element.id);
      unarchivedIds.delete(element.id);
    } else {
      ids.delete(element.id);
      unarchivedIds.add(element.id);
    }

    this.setState((state) => ({ ...state, selectedIds: ids, unarchivedIds }));
  };

  archiveElements = () => {
    let { selectedIds, allElements } = this.state;
    const selectedElements = allElements.filter((e) => selectedIds.has(e.id));

    if (selectedIds.size > 0) {
      this.setState((state) => ({
        ...state,
        openConfirmation: true,
        selectedElements,
      }));
    } else {
      this.onArchivedConfirmed();
    }
  };

  closeArchiveDialog = () => {
    this.setState((state) => ({
      ...state,
      openConfirmation: false,
      selectedElements: [],
    }));
  };

  hasSelectedIds = () => {
    const { selectedIds, unarchivedIds } = this.state;
    return selectedIds.size || unarchivedIds.size;
  };

  onArchivedConfirmed = () => {
    const { selectedIds, unarchivedIds } = this.state;

    this.setState((state) => ({
      ...state,
      openConfirmation: false,
      selectedIds: new Set(),
      unarchivedIds: new Set(),
      isLoading: this.hasSelectedIds(),
    }));

    const actions = [];

    if (unarchivedIds && unarchivedIds.size > 0) {
      const unarchiveAction = {
        action: "save",
        target: "element",
        edit_id: [...unarchivedIds].join(","),
        edit_is_active: true,
      };
      actions.push(unarchiveAction);
    }

    if (selectedIds && selectedIds.size > 0) {
      const archiveAction = {
        action: "save",
        target: "element",
        edit_id: [...selectedIds].join(","),
        edit_is_active: false,
      };
      actions.push(archiveAction);
    }

    actions.forEach((action) => {
      if (hasItems(action.edit_id)) {
        this.controller.cardActionWithCallback(
          action,
          () => this.setState((state) => ({ ...state, isLoading: false })),
          () => this.setState((state) => ({ ...state, isLoading: false })),
        );
      }
    });
  };

  render() {
    const { isLoading, elements, openConfirmation, selectedElements } =
      this.state;

    return (
      <View
        style={[appStyles.container, { backgroundColor: AppTheme.containerBg }]}
      >
        <ManaConfirmDialog
          isOpen={openConfirmation}
          title="Archive these elements?"
          description=""
          onConfirm={this.onArchivedConfirmed}
          onClose={this.closeArchiveDialog}
        >
          <ScrollView
            style={{
              paddingTop: 10,
              maxHeight: 400,
              borderTopColor: "rgba(60, 60, 67, 0.29)",
              borderTopWidth: 1,
            }}
          >
            <ElementsList
              elements={selectedElements}
              controller={this.controller}
              noIcons
            />
          </ScrollView>
        </ManaConfirmDialog>

        <KeyboardAvoidingView
          style={appStyles.kbViewNoFooter}
          behavior="height"
        >
          <Header
            title="Archive Elements"
            subTitle="Select Elements to remove"
            controller={this.controller}
            hideAvatar
            returnPage={appPages.elementsPage}
            isLoading={isLoading}
          />

          <ScrollView
            style={[
              appStyles.scrollView,
              {
                backgroundColor: "#FFF",
                marginTop: 8,
              },
            ]}
            contentContainerStyle={appStyles.scrollViewContent}
          >
            {hasItems(elements) ? (
              <ElementsList
                elements={elements}
                controller={this.controller}
                showSelector
                onElementSelected={this.updateSelectedElements}
              />
            ) : (
              <Text style={appStyles.noResultsText}>No results found</Text>
            )}
          </ScrollView>

          <View style={[appStyles.footerOptions]}>
            <ArchiveElementsFabButton style={{ bottom: 80 }} />

            <Search
              placeholder="Search Elements"
              filterResults={this.filterResults}
              controller={this.controller}
              buttonOptions={[
                {
                  isMainButton: true,
                  disabled: !this.hasSelectedIds(),
                  text: "Update Archive",
                  onPress: () => this.archiveElements(),
                },
              ]}
            />
          </View>
        </KeyboardAvoidingView>
      </View>
    );
  }
}
